import { useCallback, useEffect, useRef, useState } from "react";
import { LayoutTemplate, Plus } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

import { toast } from "@/shared/hooks/use-toast";
import { useConfirm } from "@/shared/hooks/use-confirm";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { CARD_WALL_RADIUS } from "@/shared/ui/cardWall";
import { EmptyState } from "@/shared/ui/QueryState";
import { QueryErrorBanner } from "@/shared/ui/QueryErrorBanner";
import { Skeleton } from "@/shared/ui/skeleton";
import { cn } from "@/shared/lib/utils";

import {
  isPublishOutcomeUnknown,
  websiteService,
  type ActivityItem,
  type SiteCard,
} from "../api/websiteService";
import { ActivityFeed } from "./components/ActivityFeed";
import { SiteCardView } from "./components/SiteCard";
import { useCanManageSite } from "./hooks/useCanManageSite";
import { isLaunched } from "../model/siteStep";
import { websiteBillingText } from "../model/flowLabels";
import { WebsiteShell } from "./WebsiteShell";

/**
 * 屏 1「我的站点」:左列站点卡 + 末尾新建入口;右栏发布动态 / 舱间联动 / 积分说明(1010 起:文案生成与发布不扣积分,
 * AI 配图按张扣 `website.ai_image`,单价取 `model/flowLabels.AI_IMAGE_UNIT_CREDITS`,生成前报价)。
 * ★ 站点卡缩略图用模板列表的 thumbnailUrl 兜底(拉不到模板列表就不画图,不挡站点卡)。
 *
 * ★ 动态只拉第一个站点的(契约 `GET /sites/{id}/activity` 是站点级的,舱级没有这口);
 *   拉不到在右栏自己报错,不拖垮站点卡。
 * ★ 回滚目标不是「v{n-1}」字面:按版本列表取当前之下最近一个 `published`(跳过 `rolled_back`
 *   的坏版本),确认框里写清真正的目标;api 口径回滚会生成新版本(附录 A.12),成功后重拉列表。
 * ★ 回滚走发布网关:409「结果未知」(发布服务器超时,新版本留 pending)不画成红色失败,中性 toast + 重拉列表与动态;
 *   别的 409 照旧红色,同样重拉(版本表已经多了一行 failed)。
 */
export default function SitesPage() {
  const confirm = useConfirm();
  const canManage = useCanManageSite();
  const location = useLocation();
  const [sites, setSites] = useState<SiteCard[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activity, setActivity] = useState<ActivityItem[] | null>(null);
  const [activityError, setActivityError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setSites(await websiteService.listSites());
    } catch (e) {
      setSites(null);
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // 模板缩略图(站点卡兜底用);拉不到就算了
  const [thumbs, setThumbs] = useState<Record<string, string>>({});
  useEffect(() => {
    let cancelled = false;
    Promise.resolve()
      .then(() => websiteService.listTemplates())
      .then((list) => {
        if (cancelled || !Array.isArray(list)) return;
        setThumbs(
          Object.fromEntries(
            list.flatMap((t) => (t.thumbnailUrl ? [[t.code, t.thumbnailUrl] as const] : []))
          )
        );
      })
      .catch(() => {
        /* 没有缩略图就画渐变 */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const firstId = sites?.[0]?.id ?? null;
  const firstName = sites?.[0]?.name ?? "";
  // 第一站点换了(删掉草稿那种情况)时,在途的旧请求回来不许再落到面上。
  const firstIdRef = useRef(firstId);
  useEffect(() => {
    firstIdRef.current = firstId;
  }, [firstId]);

  const loadActivity = useCallback(async () => {
    const id = firstId;
    if (!id) return;
    setActivityError(null);
    try {
      const items = await websiteService.activity(id);
      if (firstIdRef.current === id) setActivity(items);
    } catch (e) {
      if (firstIdRef.current === id) setActivityError(e instanceof Error ? e.message : String(e));
    }
  }, [firstId]);

  // `loadActivity` 只随 firstId 变:换站先清掉旧动态再拉,别让上一站的记录挂在新站的标题下。
  useEffect(() => {
    setActivity(null);
    setActivityError(null);
    void loadActivity();
  }, [loadActivity]);

  // 壳上「发布记录」指向 /ai-website#activity;SPA 里锚点不会自己滚,动态到了之后滚一次。
  useEffect(() => {
    if (location.hash !== "#activity" || activity === null) return;
    document.getElementById("activity")?.scrollIntoView?.({ block: "start" });
  }, [location.hash, activity]);

  const rollback = async (site: SiteCard) => {
    if (site.currentVersionNo == null) return;
    setBusyId(site.id);
    try {
      const versions = await websiteService.listVersions(site.id);
      const current = site.currentVersionNo;
      const target = versions
        .filter((v) => v.publishStatus === "published" && v.versionNo < current)
        .sort((a, b) => b.versionNo - a.versionNo)[0];
      if (!target) {
        toast({
          title: "没有可回滚的版本",
          description: `v${current} 之下没有发布成功过的版本。`,
          variant: "destructive",
        });
        return;
      }
      const ok = await confirm({
        title: `回滚到 v${target.versionNo}?`,
        description: `会以 v${target.versionNo} 的内容生成一个新版本并发布,当前 v${current} 标为已回滚。`,
        confirmText: "回滚",
        variant: "warning",
      });
      if (!ok) return;
      const res = await websiteService.rollback(site.id, target.id);
      toast({
        title: "已回滚",
        description: `以 v${target.versionNo} 的内容发布为 v${res.versionNo}。`,
      });
      await load();
      await loadActivity();
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      const unknown = isPublishOutcomeUnknown(e);
      toast(
        unknown
          ? { title: "回滚结果未知,稍后看版本", description: message }
          : { title: "回滚失败", description: message, variant: "destructive" }
      );
      if (unknown || (e as { httpStatus?: unknown } | null)?.httpStatus === 409) {
        await load();
        await loadActivity();
      }
    } finally {
      setBusyId(null);
    }
  };

  // 1017:宿主没有整站「下线」口,已上线 / 发过预览的站删除 = 后端先解绑域名、删宿主站再软删(立即下线),确认框要说清。
  const remove = async (site: SiteCard) => {
    const launched = isLaunched(site);
    const hasPreview = !launched && site.currentVersionNo != null;
    const ok = await confirm(
      launched
        ? {
            title: `删除站点「${site.name}」?`,
            description:
              "官网会立即下线:宿主上的站点连同已发到官网的新闻文章一起删除,绑定的自有域名同时解绑,访问者再也打不开。站点资料、草稿和版本记录一并删除,不可恢复;以后要用需重新建站。",
            confirmText: "删除并下线",
            variant: "destructive",
          }
        : {
            title: `删除草稿「${site.name}」?`,
            description: hasPreview
              ? "已采集的资料和草稿内容会一起删掉,预览站点也会立即撤下(预览链接打不开),不可恢复。"
              : "已采集的资料和草稿内容会一起删掉,不可恢复。",
            confirmText: "删除",
            variant: "destructive",
          }
    );
    if (!ok) return;
    setBusyId(site.id);
    try {
      await websiteService.deleteSite(site.id);
      setSites((prev) => prev?.filter((s) => s.id !== site.id) ?? prev);
      toast({ title: launched ? "站点已删除,官网已下线" : "草稿已删除", description: site.name });
    } catch (e) {
      toast({
        title: "删除失败",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <WebsiteShell
      actions={
        <>
          <Button asChild variant="outline" size="sm">
            <Link to="/ai-website/templates">
              <LayoutTemplate />
              模板市场
            </Link>
          </Button>
          <Button asChild size="sm">
            <Link to="/ai-website/templates">
              <Plus />
              新建站点
            </Link>
          </Button>
        </>
      }
    >
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px]">
        <div className="space-y-4">
          {error ? (
            <QueryErrorBanner isError message={error} onRetry={() => void load()} />
          ) : sites === null ? (
            <>
              <Skeleton className="h-52 w-full" />
              <Skeleton className="h-52 w-full" />
            </>
          ) : sites.length === 0 ? (
            <EmptyState
              title="还没有站点"
              description="一句话建出会营销的官网 —— 先挑一套模板,资料从知识库自动预填。"
              action={
                <Button asChild size="sm">
                  <Link to="/ai-website/templates">挑一套模板开始</Link>
                </Button>
              }
            />
          ) : (
            <>
              {sites.map((s) => (
                <SiteCardView
                  key={s.id}
                  site={s}
                  busy={busyId === s.id}
                  canManage={canManage}
                  onRollback={(site) => void rollback(site)}
                  onDelete={(site) => void remove(site)}
                  thumbnailUrl={thumbs[s.templateCode] ?? null}
                />
              ))}
              <Link
                to="/ai-website/templates"
                className={cn(
                  "flex items-center justify-center gap-2 border border-dashed border-border px-4 py-5 text-sm text-muted-foreground transition-colors hover:border-primary hover:text-primary",
                  CARD_WALL_RADIUS
                )}
              >
                <Plus className="h-4 w-4" />
                新建站点 · 从模板市场挑一套开始
              </Link>
            </>
          )}
        </div>

        <aside className="space-y-6">
          {(sites?.length ?? 0) > 0 && (
            <ActivityFeed
              siteName={firstName}
              items={activity}
              error={activityError}
              onRetry={() => void loadActivity()}
            />
          )}
          <Integrations />
          <p className="text-2xs text-muted-foreground" data-billing-note="">
            {websiteBillingText()}
          </p>
        </aside>
      </div>
    </WebsiteShell>
  );
}

/**
 * 「与其他舱的联动」三行:静态说明。
 * 1009 C 线:按真实进度标 —— 新闻舱发官网已做(B 线:一键发布 + 官网栏目定期发,模板要带新闻列表)标「已可用」;
 * 官网留资进线索库还没做,标「即将上线」,不再写「随发布生效」(发布了也不会生效)。
 * 1010:客服舱「网页 / H5」接入点已上线 → 官网 AI 接待标「已可用」,写清怎么用(手贴嵌入代码;自动挂要等宿主)。
 */
const INTEGRATIONS: ReadonlyArray<{ title: string; detail: string; ready: boolean }> = [
  {
    title: "新闻舱 → 官网新闻",
    detail: "新闻舱文章可一键发到官网,或建「官网栏目」定期发(模板需标「新闻自动更新」)",
    ready: true,
  },
  {
    title: "客服舱 → 官网 AI 接待",
    detail:
      "建站舱官网在「上线」页一键接入,每个页面右下角自动挂 AI 客服;其他官网可在「渠道管理 → 网页 / H5」建接入点,复制嵌入代码贴到页面",
    ready: true,
  },
  {
    title: "官网留资 → 获客舱线索库",
    detail: "官网表单留资自动进线索库,来源标「官网」",
    ready: false,
  },
];

function Integrations() {
  return (
    <section aria-labelledby="website-integrations-title" className="space-y-3">
      <h2 id="website-integrations-title" className="text-sm font-semibold text-foreground">
        与其他舱的联动
      </h2>
      <ul className="space-y-2">
        {INTEGRATIONS.map((it) => (
          <li
            key={it.title}
            className="flex items-start justify-between gap-2 rounded-md border border-border/70 px-3 py-2"
          >
            <div className="min-w-0">
              <div className="text-xs font-medium text-foreground">{it.title}</div>
              <div className="text-2xs text-muted-foreground">{it.detail}</div>
            </div>
            <Badge variant="outline" className="shrink-0 normal-case tracking-normal">
              {it.ready ? "已可用" : "即将上线"}
            </Badge>
          </li>
        ))}
      </ul>
    </section>
  );
}
